import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, Bookmark, Lightbulb, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

type MarketingMasonryArticleDemoProps = {
  className?: string;
};

const demoArticle = {
  title: "The unfair advantage for creators in 2026",
  feedName: "Marketing Brew",
  category: "marketing",
  publishedAt: "2025-11-14",
  description:
    "World-class marketing teams are quietly adopting creative pods, brand voice audits, and AI QA assistants to ship faster without losing their tone.",
  image:
    "https://images.unsplash.com/photo-1522252234503-e356532cafd5?auto=format&fit=crop&w=600&q=60",
};

const categoryLabelMap: Record<string, string> = {
  marketing: "Marketing",
  strategy: "Strategy",
  branding: "Branding",
};

export const MarketingMasonryArticleDemo = ({ className }: MarketingMasonryArticleDemoProps) => {
  const [imageSrc, setImageSrc] = useState(demoArticle.image);

  const handleButtonClick = () => {
    if (typeof window !== 'undefined' && window.plausible) {
      window.plausible("Clicked Demo Article Buttons");
    }
    setImageSrc((prev) => prev === demoArticle.image ? "/the-office.gif" : demoArticle.image);
  };

  return (
    <Card className={cn("overflow-hidden group", className)}>
      <div className="relative overflow-hidden">
        <img
          src={imageSrc}
          alt={demoArticle.title}
          className="w-full h-44 object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background/95 via-background/10 to-transparent" />
        <div className="absolute bottom-4 left-4 flex items-center gap-2">
          <Badge variant="secondary" className="bg-background/70 backdrop-blur">
            {categoryLabelMap[demoArticle.category] ?? demoArticle.category}
          </Badge>
          <span className="text-xs text-muted-foreground bg-background/60 px-2 py-1 rounded-full backdrop-blur">
            Sep 17
          </span>
        </div>
      </div>

      <CardHeader className="space-y-2">
        <CardTitle className="text-lg leading-tight">
          {demoArticle.title}
        </CardTitle>
        <CardDescription className="text-sm">{demoArticle.feedName}</CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        <div className="space-y-2">
          <div className="h-3 w-11/12 rounded bg-foreground/10" />
          <div className="h-3 w-3/6 rounded bg-foreground/10" />
          <div className="h-3 w-3/5 rounded bg-foreground/10" />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" className="gap-1.5 text-xs h-9" onClick={handleButtonClick}>
            <Lightbulb className="h-3.5 w-3.5" />
            Make ideas
          </Button>
          <Button size="sm" variant="outline" className="gap-1.5 text-xs h-9" onClick={handleButtonClick}>
            <Bookmark className="h-3.5 w-3.5" />
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default MarketingMasonryArticleDemo;
